import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useGetNcsPartnersQuery } from "../../../../store/appApi";

// Static fallback only — used before the live partner registry has loaded,
// or if a fixed deployment sets REACT_APP_NETPLUS_CS_PARTNERS. The picker
// itself is driven by the actual registered-partners list below — every
// registered partner, regardless of which project is currently active (a
// partner code is its own tenant key, not tied to one project).
export const PARTNER_CODES = (
  process.env.REACT_APP_NETPLUS_CS_PARTNERS ||
  "CHDGBO0204,CHDGBO0240,CHDGBO0241"
)
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

export const DEFAULT_PARTNER = PARTNER_CODES[PARTNER_CODES.length - 1];

const PARTNER_KEY = "ncsPartnerCode";
const PARTNER_EVENT = "ncs-partner-change";

// Falls back to the static list (not "") when nothing's stored yet — e.g.
// the Upload page's one-time "register a new partner" flow needs a real
// code to start from, not a blank state.
const readStoredPartner = () => {
  try {
    return window.localStorage.getItem(PARTNER_KEY) || DEFAULT_PARTNER;
  } catch {
    return DEFAULT_PARTNER;
  }
};

const writeStoredPartner = (v) => {
  try {
    window.localStorage.setItem(PARTNER_KEY, v);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(PARTNER_EVENT));
};

// Every registered partner, regardless of the caller's active project (the
// query passes ?all=true so `listPartners` skips its project-scoping). This
// is what the picker actually offers.
export const useProjectPartners = () => {
  const { data, isFetching } = useGetNcsPartnersQuery();
  const partners = useMemo(() => data?.partners || [], [data]);
  const codes = useMemo(() => partners.map((p) => p.partnerCode), [partners]);
  return { partners, codes, isFetching, loaded: Boolean(data) };
};

// Shared, localStorage-backed selected partner. All Customer Status pages read
// the same value and re-render when any picker changes it. Auto-snaps to a
// real registered partner the moment the list is known, instead of silently
// sitting on a stale/nonexistent code.
export const usePartnerCode = () => {
  const { codes, loaded } = useProjectPartners();
  const [code, setCode] = useState(readStoredPartner);

  useEffect(() => {
    const sync = () => setCode(readStoredPartner());
    window.addEventListener(PARTNER_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PARTNER_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const codesKey = codes.join("|");
  useEffect(() => {
    if (!loaded) return;
    // No partner registered for this project — nothing valid to snap to.
    if (!codes.length) return;
    if (!codes.includes(code)) {
      writeStoredPartner(codes[0]);
      setCode(codes[0]);
    }
    // codesKey is the stable dependency; codes itself is a new array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, codesKey, code]);

  const update = useCallback((v) => {
    writeStoredPartner(v);
  }, []);

  return [code, update];
};

// `allowAll` adds an "All partners" option (value "ALL") ahead of the real
// codes — the sentinel the backend's cross-partner endpoints already use
// (see listFollowups / buildDailyActivityReport). Screens whose backend
// query doesn't special-case "ALL" should leave this off.
export const PartnerPicker = ({ value, onChange, className = "", allowAll = false }) => {
  const { codes, loaded } = useProjectPartners();
  const options = codes.length ? codes : PARTNER_CODES;
  return (
    <label
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2 py-1 ${className}`}
      style={{ backgroundColor: "#fff" }}
      title="Partner code"
    >
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        Partner
      </span>
      {loaded && !codes.length && !allowAll ? (
        <span className="text-xs font-semibold text-rose-600">
          No partner registered for this project
        </span>
      ) : (
        <select
          className="bg-white text-xs font-semibold text-gray-800 outline-none"
          style={{ backgroundColor: "#fff", color: "#1f2937" }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {allowAll && (
            <option value="ALL" style={{ color: "#1f2937", backgroundColor: "#fff" }}>
              All partners
            </option>
          )}
          {options.map((c) => (
            <option
              key={c}
              value={c}
              style={{ color: "#1f2937", backgroundColor: "#fff" }}
            >
              {c}
            </option>
          ))}
        </select>
      )}
    </label>
  );
};

export const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
export const fieldClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20";

// White <select> / <input> that stays light even when the OS/browser is in
// dark mode — the native control otherwise renders black-on-black. Pair the
// class with the inline styles (Tailwind alone can't beat the UA dark stylesheet
// on a native select) and give each <option> WHITE_OPTION_STYLE.
export const selectWhiteClass =
  "rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-800 outline-none";
export const WHITE_CONTROL_STYLE = { backgroundColor: "#fff", color: "#1f2937" };
export const WHITE_OPTION_STYLE = { backgroundColor: "#fff", color: "#1f2937" };
export const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500";
export const btnPrimary =
  "rounded-full bg-cyan-600 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-cyan-500 disabled:opacity-50";
export const btnGhost =
  "rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-cyan-400 hover:text-cyan-700";

export const STATUS_TONE = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-amber-50 text-amber-700 border-amber-200",
  Suspended: "bg-rose-50 text-rose-700 border-rose-200",
  Terminated: "bg-gray-100 text-gray-500 border-gray-200",
};

// Modem-recovery case states. "not-recovered" is the legacy value — shown as
// "Untraceable" now.
export const MODEM_STATE_LABEL = {
  open: "Open",
  reopened: "Reopened",
  recovered: "Recovered",
  recharged: "Recharged",
  untraceable: "Untraceable",
  "not-recovered": "Untraceable",
};
export const MODEM_STATE_TONE = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  reopened: "bg-amber-50 text-amber-700 border-amber-200",
  recovered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  recharged: "bg-cyan-50 text-cyan-700 border-cyan-200",
  untraceable: "bg-rose-50 text-rose-700 border-rose-200",
  "not-recovered": "bg-rose-50 text-rose-700 border-rose-200",
};

export const TRANSITION_TONE = {
  RECOVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  RENEWED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  LAPSED: "bg-amber-50 text-amber-700 border-amber-200",
  AGED: "bg-amber-50 text-amber-700 border-amber-200",
  CHURNED: "bg-rose-50 text-rose-700 border-rose-200",
  DROPPED: "bg-rose-50 text-rose-700 border-rose-200",
  NEW_ACTIVE: "bg-cyan-50 text-cyan-700 border-cyan-200",
  NEW_TERMINATED: "bg-gray-100 text-gray-500 border-gray-200",
  REAPPEARED: "bg-cyan-50 text-cyan-700 border-cyan-200",
};

// Ordered to match the operational priority the roster is shown in:
// Inactive → Suspended → Active → Terminated (last), then the derived buckets.
export const CUSTOMER_BUCKETS = [
  { key: "", label: "All" },
  { key: "inactive", label: "Inactive" },
  { key: "suspended", label: "Suspended" },
  { key: "active", label: "Active" },
  { key: "terminated", label: "Terminated" },
  { key: "expiring7", label: "Expiring ≤7d" },
  { key: "overdue", label: "Overdue" },
  { key: "dropped", label: "Off sheet" },
  { key: "duplicate", label: "Duplicate" },
];

export const FOLLOWUP_STATES = [
  { key: "", label: "Open (all)" },
  { key: "open", label: "New" },
  { key: "called", label: "Called" },
  { key: "awaiting-confirmation", label: "Awaiting" },
  { key: "reopened", label: "Reopened" },
  { key: "closed", label: "Closed" },
];

export const TRANSITION_TYPES = [
  "LAPSED",
  "RECOVERED",
  "RENEWED",
  "AGED",
  "CHURNED",
  "NEW_ACTIVE",
  "NEW_TERMINATED",
  "DROPPED",
  "REAPPEARED",
];

export const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// Human labels for NetplusAuditLog `action` verbs — used by the customer
// journey timeline (CustomerDrawer) and anywhere else an audit action needs
// a readable name instead of the raw dotted key.
export const AUDIT_ACTION_LABEL = {
  "followup.assign": "Assigned",
  "followup.call": "Call logged",
  "followup.mark-paid": "Marked paid",
  "customer.note": "Remark added",
  "modem-recovery.transfer": "Transferred to modem recovery",
  "modem-recovery.bulk-transfer": "Transferred to modem recovery (bulk)",
  "modem-recovery.bulk-excel-action": "Modem action (sheet upload)",
  "modem-recovery.outcome": "Modem outcome set",
  "modem-recovery.handover": "Modem handed over",
  "modem-recovery.bulk-handover": "Modem handed over (bulk)",
  "modem-recovery.remark": "Modem remark added",
  "modem-recovery.reopen": "Modem case reopened",
  "modem-recovery.cancel-transfer": "Moved back from modem recovery",
  "modem-recovery.untraceable": "Modem marked untraceable",
  "modem-recovery.from-terminated": "Recovered from terminated",
};

export const auditActionLabel = (action) =>
  AUDIT_ACTION_LABEL[action] ||
  String(action || "")
    .split(/[.-]/)
    .filter(Boolean)
    .join(" ");

export const fmtDateInput = (d = new Date()) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(
    x.getDate(),
  ).padStart(2, "0")}`;
};

// "Diksha Sharma" -> "Diksha" — first-name-only for compact activity lines.
export const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "";

export const Badge = ({ tone, children }) => (
  <span
    className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
      tone || "bg-gray-100 text-gray-600 border-gray-200"
    }`}
  >
    {children}
  </span>
);
